'use client'

import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { useEffect, useState } from 'react'
import {
  Bar,
  BarChart,
  Label,
  LabelList,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
  XAxis,
} from 'recharts'
import RoundDisplay from '../components/roundDisplay'
import RoundSelector from '../components/roundSelector'

// TODO @Nikhil Fix this on the round view - float type error

export default function LapsPerDriverChart({ round, seasonId, overallData }) {
  const [localRound, setLocalRound] = useState(round)
  const [chartData, setChartData] = useState()

  useEffect(() => {
    function formatData(data) {
      const result = {}

      data?.forEach((d) => {
        if (result[d.round_name]) {
          result[d.round_name].valid =
            result[d.round_name].valid + parseFloat(d.laps_completed)
          result[d.round_name].invalid =
            result[d.round_name].invalid +
            parseFloat(d.laps_attempted) -
            parseFloat(d.laps_completed)
        } else {
          result[d.round_name] = {
            valid: parseFloat(d.laps_completed),
            invalid:
              parseFloat(d.laps_attempted) - parseFloat(d.laps_completed),
          }
        }
      })
      let cd = Object.entries(result).map(([key, value]) => {
        return {
          round: key,
          valid:
            value.valid / data.filter((d) => d.laps_attempted != null).length,
          invalid:
            value.invalid / data.filter((d) => d.laps_attempted != null).length,
        }
      })

      cd.map((d) => {
        d.valid = d.valid.toPrecision(3)
        d.invalid = d.invalid.toPrecision(3)
      })

      setChartData(cd)
    }
    let data = overallData
    if (localRound !== 'All Rounds') {
      data = data.filter((d) => d.event_id === localRound.event_id)
    }
    formatData(data)
  }, [localRound, overallData])

  useEffect(() => {
    setLocalRound(round)
  }, [round])

  const chartConfig = {
    valid: {
      label: 'Valid Laps',
      color: '#FF6100',
    },
    invalid: {
      label: 'Invalid Laps',
      color: '#FF6100',
    },
  }

  return (
    <div className="flex h-full flex-col items-center justify-between gap-4">
      <div className="w-full">
        <div className="flex items-center justify-between">
          <h2 className="text-p-2 font-semibold tablet:text-h-5">
            Ave. Laps per Driver
          </h2>
          <RoundSelector round={localRound} setRound={setLocalRound} />
        </div>
        <p className="mb-5 w-full text-left text-p-1 text-neutral-blue-500">
          The average number of laps each driver attempted
        </p>
        <div className="divide bg-neutral-blue-700"></div>
      </div>

      {chartData && (
        <>
          {chartData.length > 1 ? (
            <ChartContainer
              config={chartConfig}
              className="h-full max-h-full min-h-[100px] w-full max-tablet:aspect-[1.3]"
            >
              <BarChart accessibilityLayer label data={chartData}>
                <defs>
                  <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6100" />
                    <stop offset="50%" stopColor="#FF4F00" />
                    <stop offset="100%" stopColor="#FF3D00" />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="round"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  tickFormatter={(value, index) => `R${index + 1}`}
                  style={{ fill: '#627C9A' }}
                />
                <ChartTooltip
                  cursor={false}
                  content={() => <ChartTooltipContent hideLabel />}
                />

                <Bar
                  dataKey="invalid"
                  stackId={'a'}
                  fill="#FFB88C"
                  radius={window.innerWidth > 768 ? [4, 4, 4, 4] : [2, 2, 2, 2]}
                >
                  <LabelList
                    position="top"
                    offset={10}
                    className="fill-shades-0"
                    fontSize={window.innerWidth > 768 ? 12 : 8}
                  />
                </Bar>
                <Bar
                  dataKey="valid"
                  stackId={'b'}
                  fill="url(#gradient)"
                  radius={window.innerWidth > 768 ? [4, 4, 4, 4] : [2, 2, 2, 2]}
                >
                  <LabelList
                    position="top"
                    offset={10}
                    className="fill-shades-0"
                    fontSize={window.innerWidth > 768 ? 12 : 8}
                  />
                </Bar>
                <ChartLegend
                  content={() => (
                    <div className="flex items-center justify-center gap-2">
                      <div className="flex items-center justify-center gap-2">
                        <section className="size-2 rounded-[2px] bg-[#FFB88C]"></section>
                        <p className="text-p-1 text-neutral-blue-500">
                          Invalid Laps
                        </p>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <section className="size-2 rounded-[2px] bg-primary"></section>
                        <p className="text-p-1 text-neutral-blue-500">
                          Valid Laps
                        </p>
                      </div>
                    </div>
                  )}
                />
              </BarChart>
            </ChartContainer>
          ) : (
            <>
              <ChartContainer config={chartConfig}>
                <RadialBarChart
                  data={chartData}
                  endAngle={180}
                  innerRadius={window.innerWidth > 768 ? '100%' : '130%'}
                  outerRadius={window.innerWidth > 768 ? '60%' : '40%'}
                  className="w-full translate-y-[20%]"
                >
                  <ChartTooltip
                    cursor={false}
                    content={() => <ChartTooltipContent hideLabel />}
                  />
                  <PolarRadiusAxis
                    tick={false}
                    tickLine={false}
                    axisLine={false}
                  >
                    <Label
                      content={({ viewBox }) => {
                        if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                          return (
                            <text
                              x={viewBox.cx}
                              y={viewBox.cy}
                              textAnchor="middle"
                            >
                              <tspan
                                x={viewBox.cx}
                                y={viewBox.cy || 0}
                                className="fill-shades-0 text-2xl font-bold desktop-xs:text-3xl"
                              >
                                {(
                                  Number(chartData?.[0].valid) +
                                  Number(chartData?.[0].invalid)
                                ).toFixed(2)}
                              </tspan>
                              <tspan
                                x={viewBox.cx}
                                y={(viewBox.cy || 0) + 18}
                                className="fill-neutral-blue-500"
                              >
                                Total Laps
                              </tspan>
                            </text>
                          )
                        }
                      }}
                    />
                  </PolarRadiusAxis>
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF6100" />
                      <stop offset="50%" stopColor="#FF4F00" />
                      <stop offset="100%" stopColor="#FF3D00" />
                    </linearGradient>
                  </defs>
                  <RadialBar
                    dataKey="invalid"
                    stackId="a"
                    cornerRadius={5}
                    fill="#FFB88C"
                    className="stroke-transparent stroke-2"
                  />
                  <RadialBar
                    dataKey="valid"
                    fill="url(#gradient)"
                    stackId="a"
                    cornerRadius={5}
                    className="stroke-transparent stroke-2"
                  />
                </RadialBarChart>
              </ChartContainer>
              <div>
                <div className="flex items-center justify-center gap-2">
                  <div className="flex items-center justify-center gap-2">
                    <section className="size-2 rounded-[2px] bg-[#FFB88C]"></section>
                    <p className="text-p-1 text-neutral-blue-500">
                      Invalid Laps
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <section className="size-2 rounded-[2px] bg-primary"></section>
                    <p className="text-p-1 text-neutral-blue-500">Valid Laps</p>
                  </div>
                </div>
              </div>
              <RoundDisplay round={localRound}></RoundDisplay>
            </>
          )}
        </>
      )}
    </div>
  )
}
